import { useEffect, useMemo } from 'react'
import { useTourHandler } from '@/contexts/tour-handler'
import { useQuickHelp, QuickHelpOption } from '@/contexts/quick-help'
import CreditModules from '../Credits/components'
import Invoices from '../Invoices'
import useCustomerInvoicesTour from '@/modules/Customer/hooks/use-customer-invoices-tour'

/**
 * InvoicesTab - Wrapper component for invoices tab with tour support
 * Includes: Credit Summary, Invoices list, Credit transactions
 */
const InvoicesTab = () => {
    const { registerHandler, unregisterHandler } = useTourHandler()
    const { startTour } = useCustomerInvoicesTour()

    // Quick Help options for Invoices tab
    const quickHelpOptions: QuickHelpOption[] = useMemo(
        () => [
            {
                id: 'add-credit',
                label: 'Nạp tiền ký quỹ',
                description: 'Thêm hoặc hoàn tiền vào ví',
                selector: '[data-tour="credit-actions"]',
                tourTitle: 'Thêm/ Hoàn Tiền Ký Quỹ',
                tourDescription:
                    'Click "Thêm/ Hoàn Tiền" để nạp thêm tiền vào ví ký quỹ của khách hàng:\n• Chọn ví hoặc tạo ví mới\n• Chọn loại giao dịch (Nạp tiền/Hoàn tiền)\n• Nhập số tiền và ghi chú',
            },
            {
                id: 'edit-credit',
                label: 'Chỉnh sửa ví ký quỹ',
                description: 'Cập nhật thông tin ví',
                selector: '[data-tour="credit-wallet-select"]',
                tourTitle: 'Chỉnh Sửa Ví Ký Quỹ',
                tourDescription:
                    'Để chỉnh sửa thông tin ví:\n• Chọn ví cụ thể từ dropdown (không chọn "Tất cả")\n• Nút "Chỉnh sửa" sẽ xuất hiện\n• Click để cập nhật tên ví, thông tin thanh toán',
            },
            {
                id: 'merge-invoices',
                label: 'Gộp hóa đơn',
                description: 'Gộp nhiều hóa đơn thành một',
                selector: '[data-tour="invoices-merge"]',
                tourTitle: 'Gộp Hóa Đơn',
                tourDescription:
                    'Gộp nhiều hóa đơn thành một hóa đơn duy nhất:\n• Chọn ít nhất 2 hóa đơn bằng checkbox\n• Click nút "Gộp hóa đơn"\n• Chỉ gộp được hóa đơn cùng trạng thái thanh toán',
            },
            {
                id: 'view-statistics',
                label: 'Xem thống kê hóa đơn',
                description: 'Tổng quan doanh thu, thanh toán',
                selector: '[data-tour="invoices-statistical"]',
                tourTitle: 'Thống Kê Hóa Đơn',
                tourDescription:
                    'Xem tổng quan thống kê hóa đơn của khách hàng:\n• Tổng số hóa đơn\n• Đã thanh toán / Chưa thanh toán\n• Đã hủy',
            },
            {
                id: 'credit-transactions',
                label: 'Xem lịch sử giao dịch ký quỹ',
                description: 'Nạp tiền, hoàn tiền, thanh toán',
                selector: '[data-tour="credit-transactions"]',
                tourTitle: 'Lịch Sử Giao Dịch',
                tourDescription:
                    'Xem chi tiết các giao dịch ký quỹ:\n• Nạp tiền, hoàn tiền\n• Thanh toán hóa đơn từ ví\n• Thời gian, số tiền, người thực hiện',
            },
        ],
        []
    )

    useQuickHelp(quickHelpOptions)

    // Register tour handler when component mounts
    useEffect(() => {
        registerHandler(startTour)
        return () => unregisterHandler()
    }, [registerHandler, unregisterHandler, startTour])

    return (
        <>
            <CreditModules.SummaryCard />
            <Invoices />
            <CreditModules.List />
        </>
    )
}

export default InvoicesTab
